import { BaseClient } from "../../clients/base/BaseClient";
import { SERVICE_ENDPOINTS } from "./service.consts";
import type { ServiceInfo, LogoFormat, ClearCacheResponse } from "./service.types";

/**
 * Service API Client
 * Handles all service information and observability operations.
 */
export class ServiceClient extends BaseClient {
  /**
   * Fetch internal database version.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<string>} Database migration version string.
   */
  async fetchDatabaseVersion(signal?: AbortSignal | null): Promise<string> {
    return this.get<string>(SERVICE_ENDPOINTS.DB_VERSION, {
      signal,
    });
  }

  /**
   * Fetch database server version.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<string>} PostgreSQL version string.
   */
  async fetchDatabaseServerVersion(signal?: AbortSignal | null): Promise<string> {
    return this.get<string>(SERVICE_ENDPOINTS.DB_SERVER_VERSION, {
      signal,
    });
  }

  /**
   * Fetch Core application information.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ServiceInfo>} Core application info.
   */
  async fetchServiceInfo(signal?: AbortSignal | null): Promise<ServiceInfo> {
    return this.get<ServiceInfo>(SERVICE_ENDPOINTS.INFO, {
      signal,
    });
  }

  /**
   * Fetch Adapter application information.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ServiceInfo>} Adapter application info.
   */
  async fetchAdapterInfo(signal?: AbortSignal | null): Promise<ServiceInfo> {
    return this.get<ServiceInfo>(SERVICE_ENDPOINTS.ADAPTER_INFO, {
      signal,
    });
  }

  /**
   * Fetch application logo (default SVG format).
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<Blob>} Logo image as Blob.
   */
  async fetchLogo(signal?: AbortSignal | null): Promise<Blob> {
    return this.get<Blob>(SERVICE_ENDPOINTS.LOGO, {
      signal,
    });
  }

  /**
   * Fetch application logo in specific format.
   * @param {LogoFormat} format - Logo format (svg or png).
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<Blob>} Logo image as Blob.
   */
  async fetchLogoByFormat(format: LogoFormat, signal?: AbortSignal | null): Promise<Blob> {
    return this.get<Blob>(SERVICE_ENDPOINTS.LOGO_FORMAT(format), {
      signal,
    });
  }

  /**
   * Clear Core cache.
   * @returns {Promise<ClearCacheResponse>} Cache clear result or empty response.
   */
  async clearCoreCache(): Promise<ClearCacheResponse> {
    return this.delete<ClearCacheResponse>(SERVICE_ENDPOINTS.CACHE);
  }
}
